import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Tag, Alert, Space, Typography, Spin, Grid, Button, message } from 'antd';
import { SafetyCertificateOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { efirmaFiscalService, programacionDescargasService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const CONSENTIMIENTO = {
  sin_solicitar: { color: 'default', label: 'Sin solicitar' },
  solicitado: { color: 'orange', label: 'Solicitado, sin responder' },
  otorgado: { color: 'green', label: 'Otorgado' },
  revocado: { color: 'red', label: 'Revocado' },
};

/**
 * Estado de la e.firma de un contribuyente de la cartera.
 *
 * No muestra ni permite descargar material: solo si está cargada, si el
 * contribuyente autorizó y, sobre todo, si se puede exportar. Ese último dato
 * es el que el operador necesita antes de intentar nada con Orquestax.
 */
/**
 * `embebido`: dentro de la ficha, la sección plegable ya pone marco y título.
 * Repetirlos duplica bordes y padding, que en un teléfono es ancho perdido.
 */
const EfirmaPanel = ({ empresa, embebido = true }) => {
  const screens = useBreakpoint();
  const esMovil = !screens.md;
  const { isAdmin } = useAuth();
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [programando, setProgramando] = useState(false);

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa?.id]);

  const cargar = async () => {
    if (!empresa?.id) return;
    setCargando(true);
    setError('');

    try {
      setEstado(await efirmaFiscalService.getCompanyStatus(empresa.id));
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo cargar el estado de la e.firma');
    } finally {
      setCargando(false);
    }
  };

  /**
   * Programa —o reactiva— la descarga de esta empresa. Es una tanda de una,
   * sobre el mismo endpoint del proceso masivo: no hay una segunda ruta con una
   * segunda regla de alcance que pueda divergir.
   *
   * El alcance del fiscal-admin es su cartera y el del Admin es global, así que
   * la ruta se elige por perfil. Un Admin por la ruta fiscal recibiría 403: su
   * "cartera" está vacía por definición.
   */
  const programar = async () => {
    if (!empresa?.id) return;
    setProgramando(true);

    try {
      const salida = isAdmin
        ? await programacionDescargasService.procesarAdmin([empresa.id])
        : await programacionDescargasService.procesarFiscal([empresa.id]);

      const resultado = salida?.resultados?.[0];

      if (resultado?.estado === 'sin_efirma_en_sw') {
        message.warning('SW no tiene la e.firma de este RFC: hay que volver a cargarla.');
      } else if (resultado?.estado === 'fallido') {
        message.error(resultado.motivo || 'No se pudo programar la descarga');
      } else {
        message.success('Descargas programadas');
      }

      await cargar();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo programar la descarga');
    } finally {
      setProgramando(false);
    }
  };

  if (cargando) {
    return <Card title="e.firma"><Spin /> <Text type="secondary">Cargando…</Text></Card>;
  }

  if (error) {
    return <Card title="e.firma"><Alert type="error" message={error} showIcon /></Card>;
  }

  const consent = CONSENTIMIENTO[estado?.consentimiento?.estado] || CONSENTIMIENTO.sin_solicitar;

  return (
    <Card
      title={embebido ? null : <span><SafetyCertificateOutlined /> e.firma</span>}
      variant={embebido ? 'borderless' : 'outlined'}
      styles={{ body: { padding: embebido ? 0 : undefined } }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Lo primero es si se puede exportar: es la pregunta que trae al
            operador a esta tarjeta. */}
        {estado?.exportable ? (
          <Alert
            type="success"
            showIcon
            message="Disponible para exportar"
            description="El contribuyente autorizó y su e.firma está activa."
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            message="No disponible para exportar"
            description={
              !estado?.efirma?.cargada
                ? 'Este contribuyente no tiene e.firma cargada.'
                : estado?.consentimiento?.estado === 'solicitado'
                  ? 'Se le solicitó la autorización por correo y todavía no responde.'
                  : estado?.consentimiento?.estado === 'revocado'
                    ? 'El contribuyente retiró su autorización.'
                    : estado?.consentimiento?.estado === 'sin_solicitar'
                      ? 'Falta solicitar su autorización.'
                      : 'Su e.firma fue eliminada y permanece resguardada sin poder usarse.'
            }
          />
        )}

        <Descriptions column={{ xs: 1, sm: 2 }} bordered={!esMovil} size="small">
          <Descriptions.Item label="e.firma cargada">
            {estado?.efirma?.cargada
              ? <Tag color={estado.efirma.activa ? 'green' : 'orange'}>{estado.efirma.activa ? 'Sí, activa' : 'Sí, inactiva'}</Tag>
              : <Tag>No</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Consentimiento">
            <Tag color={consent.color}>{consent.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Última carga">
            {estado?.efirma?.ultima_carga || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Autorizado el">
            {estado?.consentimiento?.otorgado_en || '—'}
          </Descriptions.Item>
        </Descriptions>

        {/* PROGRAMACIÓN DE DESCARGAS. Las fechas vienen de SW, no de lo que
            mifactura recuerde haber hecho: son la única prueba de que la
            programación está trabajando y no solo existiendo. */}
        {estado?.efirma?.cargada && estado?.efirma?.activa && (
          <div>
            <Text strong style={{ fontSize: 13 }}>Programación de descargas</Text>
            {estado?.programacion_descargas?.consultado ? (
              <Descriptions column={1} size="small" style={{ marginTop: 4 }}>
                {[
                  ['Emitidos', estado.programacion_descargas.emitidos],
                  ['Recibidos', estado.programacion_descargas.recibidos],
                ].map(([etiqueta, prog]) => (
                  <Descriptions.Item label={etiqueta} key={etiqueta}>
                    {/* Una programación apagada se pinta como NO programada: un
                        verde con matices dejaría al RFC pareciendo resuelto sin
                        descargar nada. */}
                    <Tag color={prog?.programada ? 'green' : 'orange'}>
                      {prog?.programada ? 'Activa' : 'No programada'}
                    </Tag>
                    {prog?.programada && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        última {prog.ultima || '—'} · próxima {prog.proxima || '—'}
                      </Text>
                    )}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            ) : (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  No se pudo consultar en SW. Se desconoce si está programada.
                </Text>
              </div>
            )}
            {/* Falta alguna de las dos —o está apagada, que es lo mismo: no
                descarga—, así que se ofrece resolverlo aquí sin ir al proceso
                masivo. */}
            {estado?.programacion_descargas?.consultado
              && (!estado.programacion_descargas.emitidos?.programada
                || !estado.programacion_descargas.recibidos?.programada) && (
              <div style={{ marginTop: 8 }}>
                <Button
                  size="small"
                  icon={<CloudDownloadOutlined />}
                  loading={programando}
                  disabled={programando}
                  onClick={programar}
                >
                  {estado.programacion_descargas.emitidos?.programada
                    || estado.programacion_descargas.recibidos?.programada
                    ? 'Reactivar descargas'
                    : 'Programar descargas'}
                </Button>
              </div>
            )}
          </div>
        )}

        <Text type="secondary" style={{ fontSize: 12 }}>
          El material de la e.firma nunca se muestra aquí. La exportación se hace desde
          Orquestax, hacia un dispositivo autorizado, y queda registrada en la bitácora.
        </Text>
      </Space>
    </Card>
  );
};

export default EfirmaPanel;
